import { getHasAccessToLoggedOutInsiderMarketingPage } from "@faire/deprecated-retailer-visitor-shared/hooks/insider/getHasAccessToLoggedOutInsiderMarketingPage";
import { useNationality } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/useNationality";
import { RetailerRenderWithBuilder } from "@faire/deprecated-retailer-visitor-shared/lib/BuilderIO/RetailerRenderWithBuilder";
import { assignInlineVars } from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { WeMakeItSimpleRightImage } from "@faire/deprecated-retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/WeMakeItSimpleBanner/__internal__/WeMakeItSimpleRightImage";
import { trackLoggedOutRetailersHomePageBannerInsiderMembershipButtonClick } from "@faire/web-api--source/events/loggedOutRetailers/click/homePageBannerInsiderMembershipButton";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import insiderMarketingPage from "@faire/web-api--source/routes/www/insider";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Column, Flex } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { cn } from "@faire/web/tools/cn";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { type JSX } from "react";

import { useIsLoggedOutHomepageV2 } from "../../useIsLoggedOutHomepageV2";

import {
  bannerMinHeightVars,
  imageWrapperHeightVars,
  imageWrapperWidthVars,
  XLARGE_BANNER_WIDTH,
} from "./styles.css";

export const WeMakeItSimpleBanner: React.FC = () => {
  // Trace component render for homepage performance monitoring
  // Related to FD-275664: Add OTEL tracing to logged out homepage
  useTraceComponentRender("WeMakeItSimpleBanner");

  const isHomepageRedesignV2 = useIsLoggedOutHomepageV2();
  const trackEvent = () =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView(
      "we_make_it_simple_banner",
      isHomepageRedesignV2 ? "v2" : "v1"
    );
  const ref = useDoActionWhenVisible(trackEvent);
  if (isHomepageRedesignV2) {
    return (
      <RetailerRenderWithBuilder
        modelName="logged-out-homepage-new-brand-perks-banner"
        onViewEvent={trackEvent}
      />
    );
  }
  return (
    <Flex
      direction={{ mobileAndAbove: "column", tabletAndAbove: "row" }}
      className={cn("w-full bg-[#F1F29F]", bannerMinHeightVars.classes)}
      data-test-id="we-make-it-simple-banner"
      ref={ref}
      style={bannerMinHeightVars.vars}
    >
      <div
        className="w-full md:w-1/2"
        style={{ height: "100%", alignContent: "center" }}
      >
        <LeftContent />
      </div>

      <div
        style={assignInlineVars({
          ...imageWrapperWidthVars.vars,
          ...imageWrapperHeightVars.vars,
        })}
        className={cn(
          "relative w-full md:w-1/2 xl:inline-flex xl:aspect-square xl:self-center",
          imageWrapperWidthVars.classes,
          imageWrapperHeightVars.classes
        )}
      >
        <WeMakeItSimpleRightImage />
      </div>
    </Flex>
  );
};

const Paragraph = ({
  number,
  content,
}: {
  number: string;
  content: string | React.ReactNode;
}) => {
  return (
    <>
      <Typography
        variant={{
          mobileAndAbove: "subheadingSansMedium",
          tabletAndAbove: "paragraphSansMedium",
          desktopAndAbove: "subheadingSansMedium",
        }}
      >
        {number}
      </Typography>
      <Spacer size="1x" />
      <Typography
        variant={{
          mobileAndAbove: "subheadingSansRegular",
          tabletAndAbove: "paragraphSansRegular",
          desktopAndAbove: "subheadingSansRegular",
        }}
      >
        {content}
      </Typography>
    </>
  );
};

const LeftContent = () => {
  const { strictLocalize } = useStrictLocalization();
  const { retailerCountry } = useNationality();

  const getThirdParagraphContent = () => {
    if (getHasAccessToLoggedOutInsiderMarketingPage(retailerCountry)) {
      return (
        <StrictLocalMsg
          id="eKBFPY"
          defaultMessage="Unlock free shipping with thousands of brands with a Faire <LinkFormatting>Insider membership</LinkFormatting>."
          description={{
            text: "Logged out home page we make it simple content",
            img: "https://cdn.faire.com/fastly/6f2f577b59b1290ddafc39c29d6edad276d0b4d00d900281891384bbcea68be8.png",
          }}
          values={{ LinkFormatting }}
        />
      );
    } else {
      return (
        <StrictLocalMsg
          id="BWm0DT"
          defaultMessage="Unlock free shipping with thousands of brands with a Faire Insider membership."
          description={{
            text: "Logged out home page we make it simple content",
            img: "https://cdn.faire.com/fastly/6f2f577b59b1290ddafc39c29d6edad276d0b4d00d900281891384bbcea68be8.png",
          }}
        />
      );
    }
  };

  return (
    <Column
      style={{ maxWidth: `${XLARGE_BANNER_WIDTH}px` }}
      className="mx-8 my-12 lg:my-[75px] lg:mr-0 lg:ml-auto lg:pr-[60px] lg:pl-8"
    >
      <Typography
        variant={{
          mobileAndAbove: "displaySSerifRegular",
          desktopAndAbove: "displayMSerifRegular",
        }}
      >
        <StrictLocalMsg
          id="oKelKM"
          defaultMessage="We make it simple."
          description={{
            text: "Logged out home page we make it simple banner title",
            img: "https://cdn.faire.com/fastly/6f2f577b59b1290ddafc39c29d6edad276d0b4d00d900281891384bbcea68be8.png",
          }}
        />
        <Spacer width="1x" style={{ display: "inline-block" }} />
        <StrictLocalMsg
          id="Tu3+M+"
          defaultMessage="You make it yours."
          description={{
            text: "Logged out home page we make it simple banner title",
            img: "https://cdn.faire.com/fastly/6f2f577b59b1290ddafc39c29d6edad276d0b4d00d900281891384bbcea68be8.png",
          }}
        />
      </Typography>
      <Spacer size={{ mobileAndAbove: "3x", desktopAndAbove: "4x" }} />
      <Paragraph
        number="01"
        content={strictLocalize({
          id: "DNY1t0",
          defaultMessage:
            "Try new brands with confidence—you’ve got free, easy returns on all first orders.",
          description: {
            text: "Logged out home page we make it simple content",
            img: "https://cdn.faire.com/fastly/6f2f577b59b1290ddafc39c29d6edad276d0b4d00d900281891384bbcea68be8.png",
          },
        })}
      />
      <Spacer size={{ mobileAndAbove: "3x", desktopAndAbove: "4x" }} />
      <Paragraph
        number="02"
        content={strictLocalize({
          id: "NMi0Jy",
          defaultMessage:
            "Buy new inventory for your shop now and pay invoices 60 days later, with zero fees.",
          description: {
            text: "Logged out home page we make it simple content",
            img: "https://cdn.faire.com/fastly/6f2f577b59b1290ddafc39c29d6edad276d0b4d00d900281891384bbcea68be8.png",
          },
        })}
      />
      <Spacer size={{ mobileAndAbove: "3x", desktopAndAbove: "4x" }} />
      <Paragraph number="03" content={getThirdParagraphContent()} />
    </Column>
  );
};

const LinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    onClick={trackLoggedOutRetailersHomePageBannerInsiderMembershipButtonClick}
    linkTo={insiderMarketingPage()}
    target="_blank"
    style={{ fontSize: "inherit" }}
  >
    {chunks}
  </Button>
);
